import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import deepResearchStore from "Stores/deepResearchData";
import { makeApi, URLS } from "Utils/apiURL";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, FREQUENCY_OPTIONS, daysOf, scheduleText, defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

const CreateAgent = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("Daily ICP Prospector");
  const [sellerProfileId, setSellerProfileId] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [dailyCap, setDailyCap] = useState(25);
  const [requireApproval, setRequireApproval] = useState(false);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [time, setTime] = useState("08:00");
  const [frequency, setFrequency] = useState("DAILY"); // DAILY | WEEKDAYS | WEEKLY
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [fromAI, setFromAI] = useState(false);
  const timezone = defaultTimezone();
  const [saving, setSaving] = useState(false);
  const [website, setWebsite] = useState("");
  const [creatingSeller, setCreatingSeller] = useState(false);
  const [sellerError, setSellerError] = useState("");
  const [showCreateSeller, setShowCreateSeller] = useState(false);

  useEffect(() => {
    deepResearchStore.fetchSellerProfiles({ force: true });
    // Arrived from "Describe it": prefill what the description said (schedule, limits, approval).
    const draft = agentsData.consumeTemplateDraft("daily_icp_prospector");
    if (draft) {
      setFromAI(true);
      if (draft.name) setName(draft.name);
      const tc = (draft.trigger && draft.trigger.config) || {};
      if (["DAILY", "WEEKDAYS", "WEEKLY"].includes(tc.frequency)) setFrequency(tc.frequency);
      if (tc.time) setTime(tc.time);
      if (tc.frequency === "WEEKLY") setDays(daysOf(tc));
      const c = draft.config || {};
      if (c.dailyRecordCap) setDailyCap(c.dailyRecordCap);
      if (c.notifyMode) setNotifyMode(c.notifyMode);
      if (c.approvalMode === "REQUIRED") setRequireApproval(true);
    }
  }, []);

  const sellers = deepResearchStore.sellerProfiles || [];
  const canSubmit = sellerProfileId && sequenceId && name;
  const seqName = sequenceName;

  const createSeller = async () => {
    const site = website.trim();
    if (!site) return;
    setCreatingSeller(true);
    setSellerError("");
    const res = await makeApi({
      url: URLS.deepResearchEnrichSeller,
      method: "POST",
      data: { website: site }
    });
    if (res && res.data && !res.data.error) {
      const saved = await deepResearchStore.saveSellerProfile({ ...res.data, website: site });
      if (saved && saved.id) {
        setSellerProfileId(saved.id);
        setWebsite("");
        setShowCreateSeller(false);
      } else {
        setSellerError("Could not save the profile. Try again.");
      }
    } else {
      setSellerError("Couldn't build a profile from that website. Check the URL and try again.");
    }
    setCreatingSeller(false);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createDailyIcpProspector({
      name, sellerProfileId, sequenceId, dailyCap: Number(dailyCap), time, timezone,
      frequency, ...(frequency === "WEEKLY" ? { days } : {}),
      approvalMode: requireApproval ? "REQUIRED" : "NONE",
      notifyMode
    });
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const step = (done, node) => (
    <li>
      <span className={`ck ${done ? "" : "ck--todo"}`}>✓</span>
      <span>{node}</span>
    </li>
  );

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents")}>← Agents</button>
          <h1 className="ag-page__title">Hire · Daily ICP Prospector</h1>
          <p className="ag-page__sub">{fromAI
            ? "✦ Set up from your description — pick a seller profile and a sequence, then create."
            : "Answer a few questions. It finds ICP-matched people on a schedule and enrolls them."}</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Who &amp; how many</div>
          <label className="ag-field">
            <span className="ag-field__label">Seller profile (drives ICP &amp; content)<b className="ag-req">required</b></span>
            {sellers.length > 0 && (
              <AgentSelect
                value={sellerProfileId}
                placeholder="Select a seller profile…"
                searchable
                searchPlaceholder="Search seller profiles…"
                onChange={setSellerProfileId}
                options={sellers.map(s => {
                  const base = s.company_name || s.companyName || s.product_name || s.name || s.website || s.id;
                  const sub = s.product_name && s.product_name !== base ? ` — ${s.product_name}` : "";
                  return { value: s.id, label: `${base}${sub}` };
                })}
              />
            )}
            {sellers.length > 0 && !showCreateSeller && (
              <button
                type="button"
                className="ag-link"
                style={{ marginTop: 8, alignSelf: "flex-start" }}
                onClick={() => setShowCreateSeller(true)}
              >
                + Create a new seller profile
              </button>
            )}
            {sellers.length === 0 && deepResearchStore.sellerProfilesLoading && (
              <div className="ag-input" style={{ color: "#98a0b0" }}>Loading seller profiles…</div>
            )}
            {(showCreateSeller || (sellers.length === 0 && !deepResearchStore.sellerProfilesLoading)) && (
              <div className="ag-seller-create" style={{ marginTop: sellers.length > 0 ? 10 : 0 }}>
                <div className="ag-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                  Create a seller profile from your company website — we'll derive your ICP from it.
                </div>
                <div className="ag-inline-row">
                  <input
                    className="ag-input"
                    placeholder="https://yourcompany.com"
                    value={website}
                    onChange={e => setWebsite(e.target.value)}
                    disabled={creatingSeller}
                  />
                  <button
                    className="ag-btn ag-btn--primary"
                    style={{ whiteSpace: "nowrap" }}
                    disabled={creatingSeller || !website.trim()}
                    onClick={createSeller}
                  >
                    {creatingSeller ? "Building profile…" : "Create profile"}
                  </button>
                </div>
                {creatingSeller && <span className="ag-hint">Researching the site &amp; drafting your profile — this takes ~20s.</span>}
                {sellerError && <span className="ag-hint" style={{ color: "#c23a34" }}>{sellerError}</span>}
                {sellers.length > 0 && !creatingSeller && (
                  <button
                    type="button"
                    className="ag-link"
                    style={{ marginTop: 8 }}
                    onClick={() => { setShowCreateSeller(false); setSellerError(""); setWebsite(""); }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence<b className="ag-req">required</b></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>
                {sequenceName || "Select a sequence…"}
              </span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">Email sequences enrich contacts first; LinkedIn-only sequences skip enrichment.</span>
          </label>

          <div className="ag-fsec">Guardrails &amp; schedule</div>
          <div className="ag-field-row">
            <label className="ag-field">
              <span className="ag-field__label">Run</span>
              <AgentSelect value={frequency} onChange={setFrequency} options={FREQUENCY_OPTIONS} />
            </label>
            {frequency === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            <label className="ag-field">
              <span className="ag-field__label">Contacts / run</span>
              <input className="ag-input" type="number" min="1" max="200" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
            <label className="ag-field">
              <span className="ag-field__label">Run time</span>
              <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
            </label>
          </div>
          <span className="ag-hint">Runs {scheduleText(frequency, time, days, timezone)} — your time zone ({timezone.replace(/_/g, " ")}).</span>

          <label className="ag-toggle-field">
            <input
              type="checkbox"
              checked={requireApproval}
              onChange={e => setRequireApproval(e.target.checked)}
            />
            <span>
              <b>Require my approval before enrolling</b>
              <span className="ag-hint" style={{ marginTop: 2 }}>
                Each run pauses so you can review the contacts it found. Approve them from the Approvals queue before anyone is enrolled.
              </span>
            </span>
          </label>

          <label className="ag-field" style={{ marginTop: 16 }}>
            <span className="ag-field__label">Notifications</span>
            <AgentSelect
              value={notifyMode}
              onChange={setNotifyMode}
              options={[
                { value: "DAILY_DIGEST", label: "Daily digest — one summary email a day" },
                { value: "ON_FAILURE", label: "Only when a run fails" },
                { value: "OFF", label: "Off — don't email me" }
              ]}
            />
            <span className="ag-hint">
              {notifyMode === "DAILY_DIGEST"
                ? "A single rollup of the day's runs, sent each evening."
                : notifyMode === "ON_FAILURE"
                ? "An email only when a run hits errors or fails — rate-limited so it never spams."
                : "This agent won't send you any email."}
            </span>
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(!!sellerProfileId, <span>Finds <b>{dailyCap} ICP contacts</b> <b>{scheduleText(frequency, time, days, timezone)}</b></span>)}
              {step(true, <span>Skips anyone already contacted or on DNC</span>)}
              {step(true, <span>Finds &amp; validates their email</span>)}
              {requireApproval && step(true, <span>Pauses for <b>your approval</b> before enrolling</span>)}
              {step(!!sequenceId, <span>Enrolls into <b>{seqName || "your sequence"}</b></span>)}
              {notifyMode === "DAILY_DIGEST" && step(true, <span>Emails you a <b>daily digest</b></span>)}
              {notifyMode === "ON_FAILURE" && step(true, <span>Emails you <b>only when a run fails</b></span>)}
            </ul>
            <div className="ag-costbox">
              <div>
                <div className="cl">Est. cost / run</div>
                <div className="ag-hint" style={{ marginTop: 0 }}>~1 credit / contact</div>
              </div>
              <div className="cv">~{dailyCap}<span style={{ fontSize: ".7rem", color: "#6b7280" }}> cr</span></div>
            </div>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Creating…" : "Create & build ICP →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
          <p className="ag-hint" style={{ textAlign: "center" }}>Next, you'll build the ICP search in List Builder — pre-filled from the seller profile.</p>
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateAgent;
